import { Grid } from 'antd';

import { Job } from '@/client';
import { AvatarAndName } from '@/components/AvatarAndName';
import { GuardedLink } from '@/components/GuardedLink';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Card } from '@/primitives/Card';
import { Col } from '@/primitives/Col';
import { OsIcon } from '@/primitives/Icons';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { VerticalDivider } from '@/primitives/VerticalDivider';
import { tokens } from '@/theme';

export type JobDetailsConfigProps = {
  job: Job;
};

const { useBreakpoint } = Grid;

export function JobDetailsConfig({ job }: JobDetailsConfigProps) {
  const { t } = useMimicTranslation('jobs');
  const screens = useBreakpoint();
  const paddingBottom = screens.xl ? undefined : '8px';

  return (
    <Card padding="md" gap="sm">
      <Title level={3} style={{ fontSize: '17px', fontWeight: '700' }}>
        {t('targetConfig')}
      </Title>
      <div style={{ display: 'flex', width: '100%' }}>
        <Row>
          <Col flex="auto">
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}>
              <GuardedLink
                to={`/tenants/${job.tenantId}/node-configs/${job.targetConfig.nodeConfig.id}`}
                requiredRole="editor"
              >
                <Text mono>{job.targetConfig.nodeConfig.name}</Text>
              </GuardedLink>
              <Row gutter={[16, 8]} style={{ paddingBottom }}>
                <Col>
                  <Text size="sm" type="subtle" style={{ textTransform: 'capitalize' }}>
                    <OsIcon name={job.targetConfig.nodeConfig.operatingSystem} />{' '}
                    {job.targetConfig.nodeConfig.operatingSystem}
                  </Text>
                </Col>
                {screens.sm && <VerticalDivider />}
                <Col>
                  <Text mono copyable type="subtle" size="sm">
                    {job.targetConfig.nodeConfig.id}
                  </Text>
                </Col>
              </Row>
            </div>
          </Col>
          <Col flex="auto">
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}>
              <GuardedLink
                to={`/tenants/${job.tenantId}/node-configs/${job.targetConfig.nodeConfig.id}/revisions/${job.targetConfig.configRevision.revisionNumber}`}
                requiredRole="editor"
              >
                <Text>
                  {t('revision')} #{job.targetConfig.configRevision.revisionNumber}:{' '}
                  {job.targetConfig.configRevision.description}
                </Text>
              </GuardedLink>
              <Row gutter={[16, 8]}>
                <Col>
                  <AvatarAndName
                    size="sm"
                    userInfo={job.targetConfig.configRevision.createdBy}
                    color={tokens.color.text.textDefault}
                  />
                </Col>
                {screens.sm && <VerticalDivider />}
                <Col>
                  <Text mono copyable type="subtle" size="sm">
                    {job.targetConfig.configRevision.id}
                  </Text>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
}
